/**
 * Itinerary Map Component
 * React Map GL implementation with 3D mode and map style controls
 */

import React, { useRef, useEffect, useState, useMemo } from 'react';
import Map, { Marker, ScaleControl, MapRef, Source, Layer } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { Box, Typography, Chip, IconButton, Tooltip, useTheme, Paper, Divider } from '@mui/material';
import { keyframes } from '@emotion/react';
import { RouteDataEntry } from '../services/itineraryApi';
import { VehicleObservation, IndividualObservation } from '@signals/app-shell/services/observationsApi';
import MapStyleControl, { MapStyleType } from '@signals/cop-ui/components/MapStyleControl';
import { 
  ThreeDRotation, 
  Map as MapIcon, 
  ExpandMore, 
  ExpandLess, 
  DirectionsCar, 
  Person, 
  Layers as LayersIcon,
  Straighten as MeasureIcon,
  Delete as DeleteIcon,
  MyLocation as MyLocationIcon,
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
  Navigation as CompassIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Satellite as SatelliteIcon,
} from '@mui/icons-material';

// Flash animation keyframes
const flashAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 235, 59, 1);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 25px rgba(255, 235, 59, 0);
    transform: scale(1.4);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 235, 59, 0);
    transform: scale(1);
  }
`;

// GPS track point for displaying student's route
export interface TrackPoint {
  latitude: number;
  longitude: number;
  timestamp: Date;
}

/** One device's GPS track — kept separate (not merged across devices). */
export interface DeviceTrack {
  deviceId: string;
  deviceType: string;
  color: string;
  points: TrackPoint[];
}

/** Pathfinder-default device colors (phone / watch / vehicle). */
export const ITINERARY_DEVICE_TYPE_COLORS: Record<string, string> = {
  phone: '#4caf50',
  watch: '#2196f3',
  vehicle: '#9c27b0',
};

export function normalizeItineraryDeviceType(deviceType?: string | null): string {
  const key = (deviceType || '').toLowerCase();
  if (key.includes('vehicle') || key.includes('beacon')) return 'vehicle';
  if (key.includes('watch')) return 'watch';
  if (key.includes('phone') || key.includes('cell') || key.includes('samsung')) return 'phone';
  return key || 'unknown';
}

export function colorForDeviceType(deviceType?: string | null): string {
  const normalized = normalizeItineraryDeviceType(deviceType);
  return ITINERARY_DEVICE_TYPE_COLORS[normalized] || '#757575';
}

// Dwell location for displaying student's stops
export interface DwellLocation {
  latitude: number;
  longitude: number;
  start_time: Date;
  end_time: Date;
  duration_minutes: number;
  address?: string;
}

interface ItineraryMapGLProps {
  routeEntries: RouteDataEntry[];
  onMapClick?: (lat: number, lon: number, address?: string) => void;
  isLocationSelectionMode?: boolean;
  selectedEntryIndex?: number;
  mapStyle?: string;
  // For View Submissions tab
  viewMode?: 'edit' | 'view';
  onEntryClick?: (entry: any) => void;
  showLegend?: boolean;
  onMapReady?: (map: any) => void;
  // External flash trigger - index of entry to flash
  flashEntryIndex?: number | null;
  // For observations
  vehicleObservations?: VehicleObservation[];
  individualObservations?: IndividualObservation[];
  // External flash trigger for observation markers (e.g. "vehicle-5" or "individual-3")
  flashObservationId?: string | null;
  // Per-device GPS tracks and dwells (Load My Route)
  deviceTracks?: DeviceTrack[];
  dwellLocations?: DwellLocation[];
}

const ItineraryMapGL: React.FC<ItineraryMapGLProps> = ({
  routeEntries,
  onMapClick,
  isLocationSelectionMode = false,
  selectedEntryIndex = -1,
  mapStyle: initialMapStyle = 'mapbox://styles/mapbox/streets-v12',
  viewMode = 'edit',
  onEntryClick,
  showLegend: _showLegend = false,
  onMapReady,
  flashEntryIndex = null,
  vehicleObservations = [] as VehicleObservation[],
  individualObservations = [] as IndividualObservation[],
  flashObservationId = null,
  deviceTracks = [] as DeviceTrack[],
  dwellLocations = [] as DwellLocation[],
}) => {
  const mapRef = useRef<MapRef | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const geocoderContainerRef = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const theme = useTheme();
  
  // Map style state - defaults based on theme: light mode = streets, dark mode = dark
  const [mapStyle, setMapStyle] = useState<MapStyleType>(() => {
    const style = initialMapStyle.toLowerCase();
    if (style.includes('satellite-streets')) return 'satellite-streets';
    if (style.includes('satellite')) return 'satellite';
    if (style.includes('dark')) return 'dark';
    if (style.includes('light')) return 'light';
    if (style.includes('outdoors')) return 'outdoors';
    // Use theme-based default
    return theme.palette.mode === 'light' ? 'streets' : 'dark';
  });
  
  // 3D mode state
  const [is3DMode, setIs3DMode] = useState(false);
  
  // Legend visibility and collapsed state
  const [showLegendPanel, setShowLegendPanel] = useState(false);
  const [legendCollapsed, setLegendCollapsed] = useState(false);
  
  // Flash animation state
  const [flashingMarkerId, setFlashingMarkerId] = useState<number | null>(null);
  const [flashingObsId, setFlashingObsId] = useState<string | null>(null);
  
  // Measurement tool state
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState<[number, number][]>([]);
  const [totalDistance, setTotalDistance] = useState<number>(0);
  
  // Controls state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showStyleSelector, setShowStyleSelector] = useState(false);

  // Trigger flash from parent (e.g., when clicking from list)
  useEffect(() => {
    if (flashEntryIndex !== null && flashEntryIndex >= 0) {
      setFlashingMarkerId(flashEntryIndex);
      const timer = setTimeout(() => setFlashingMarkerId(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [flashEntryIndex]);

  // Trigger flash for observation markers (e.g. "vehicle-5")
  useEffect(() => {
    if (flashObservationId) {
      setFlashingObsId(flashObservationId);
      const timer = setTimeout(() => setFlashingObsId(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [flashObservationId]);

  // Convert MapStyleType to Mapbox URL
  const getMapboxStyleUrl = (style: MapStyleType): string => {
    const styleMap: Record<MapStyleType, string> = {
      'streets': 'mapbox://styles/mapbox/streets-v12',
      'satellite': 'mapbox://styles/mapbox/satellite-v9',
      'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
      'dark': 'mapbox://styles/mapbox/dark-v11',
      'light': 'mapbox://styles/mapbox/light-v11',
      'outdoors': 'mapbox://styles/mapbox/outdoors-v12',
    };
    return styleMap[style];
  };

  // Initialize geocoder - always show, not just in location selection mode
  useEffect(() => {
    if (!mapRef.current || !geocoderContainerRef.current || !mapLoaded) return;
    
    const geocoder = new MapboxGeocoder({
      accessToken: process.env.REACT_APP_MAPBOX_TOKEN || '',
      mapboxgl: mapRef.current.getMap() as any,
      marker: false,
      placeholder: 'Search for address or place...',
      proximity: {
        longitude: -110.9265,
        latitude: 32.2217
      } as any,
    });
    
    const containerElement = geocoderContainerRef.current;
    containerElement.appendChild(geocoder.onAdd(mapRef.current.getMap()));
    
    // Handle geocoder result
    geocoder.on('result', (e) => {
      const [lon, lat] = e.result.center;
      const address = e.result.place_name;
      
      // If in location selection mode, trigger the callback
      if (isLocationSelectionMode && onMapClick) {
        onMapClick(lat, lon, address);
      }
      
      // Always fly to the selected location
      mapRef.current?.flyTo({
        center: [lon, lat],
        zoom: 15,
        duration: 1500
      });
    });

    return () => {
      if (containerElement) {
        containerElement.innerHTML = '';
      }
    };
  }, [mapLoaded, isLocationSelectionMode, onMapClick]); // Only depend on mapLoaded, so geocoder is always available

  // Zoom to fit per-device GPS tracks and dwells when they load
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    const hasTrack = deviceTracks.some((t) => t.points.length > 0);
    const hasDwells = dwellLocations.length > 0;

    if (!hasTrack && !hasDwells) return;

    const bounds = new mapboxgl.LngLatBounds();

    deviceTracks.forEach((track) => {
      track.points.forEach((point) => {
        if (point.latitude && point.longitude) {
          bounds.extend([point.longitude, point.latitude]);
        }
      });
    });

    dwellLocations.forEach((dwell) => {
      if (dwell.latitude && dwell.longitude) {
        bounds.extend([dwell.longitude, dwell.latitude]);
      }
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 15,
        duration: 1000,
      });
    }
  }, [deviceTracks, dwellLocations, mapLoaded]);

  const deviceTrackGeoJSONById = useMemo(() => {
    return deviceTracks
      .filter((t) => t.points.length > 1)
      .map((track) => {
        const safeId = String(track.deviceId || track.deviceType || 'device')
          .replace(/[^a-zA-Z0-9_-]/g, '_')
          .slice(0, 64);
        return {
          sourceId: `gps-device-track-${safeId}`,
          layerId: `gps-device-track-line-${safeId}`,
          color: track.color,
          deviceType: track.deviceType,
          pointCount: track.points.length,
          data: {
            type: 'Feature' as const,
            properties: {
              deviceId: track.deviceId,
              deviceType: track.deviceType,
            },
            geometry: {
              type: 'LineString' as const,
              coordinates: track.points.map((pt) => [pt.longitude, pt.latitude]),
            },
          },
        };
      });
  }, [deviceTracks]);

  // Toggle 3D mode
  const toggle3DMode = () => {
    if (!mapRef.current) return;
    
    const map = mapRef.current.getMap();
    
    if (!is3DMode) {
      // Enable 3D
      map.easeTo({
        pitch: 60,
        bearing: 0,
        duration: 1000
      });
      
      // Add 3D buildings layer
      if (map.getLayer('add-3d-buildings')) {
        map.setLayoutProperty('add-3d-buildings', 'visibility', 'visible');
      } else {
        map.addLayer({
          'id': 'add-3d-buildings',
          'source': 'composite',
          'source-layer': 'building',
          'filter': ['==', 'extrude', 'true'],
          'type': 'fill-extrusion',
          'minzoom': 15,
          'paint': {
            'fill-extrusion-color': '#aaa',
            'fill-extrusion-height': [
              'interpolate',
              ['linear'],
              ['zoom'],
              15,
              0,
              15.05,
              ['get', 'height']
            ],
            'fill-extrusion-base': [
              'interpolate',
              ['linear'],
              ['zoom'],
              15,
              0,
              15.05,
              ['get', 'min_height']
            ],
            'fill-extrusion-opacity': 0.6
          }
        });
      }
      setIs3DMode(true);
    } else {
      // Disable 3D
      map.easeTo({
        pitch: 0,
        bearing: 0,
        duration: 1000
      });
      
      if (map.getLayer('add-3d-buildings')) {
        map.setLayoutProperty('add-3d-buildings', 'visibility', 'none');
      }
      setIs3DMode(false);
    }
  };

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  };

  // Update total distance when measurement points change
  useEffect(() => {
    if (measurementPoints.length < 2) {
      setTotalDistance(0);
      return;
    }
    
    let total = 0;
    for (let i = 1; i < measurementPoints.length; i++) {
      total += calculateDistance(
        measurementPoints[i - 1][1], measurementPoints[i - 1][0],
        measurementPoints[i][1], measurementPoints[i][0]
      );
    }
    setTotalDistance(total);
  }, [measurementPoints]);

  // Handle measurement click
  const handleMeasurementClick = (event: any) => {
    if (!isMeasuring) return;
    const { lngLat } = event;
    setMeasurementPoints(prev => [...prev, [lngLat.lng, lngLat.lat]]);
  };

  // Clear measurement
  const clearMeasurement = () => {
    setMeasurementPoints([]);
    setTotalDistance(0);
  };

  // Toggle measurement mode
  const toggleMeasurement = () => {
    if (isMeasuring) {
      setIsMeasuring(false);
    } else {
      setIsMeasuring(true);
      clearMeasurement();
    }
  };

  // Format distance for display
  const formatDistance = (km: number): string => {
    if (km < 1) {
      return `${(km * 1000).toFixed(0)} m`;
    }
    return `${km.toFixed(2)} km (${(km * 0.621371).toFixed(2)} mi)`;
  };

  // Zoom controls
  const handleZoomIn = () => {
    mapRef.current?.getMap().zoomIn();
  };

  const handleZoomOut = () => {
    mapRef.current?.getMap().zoomOut();
  };

  // Reset compass/bearing
  const handleResetBearing = () => {
    mapRef.current?.getMap().resetNorth();
  };

  // Fullscreen toggle
  const toggleFullscreen = () => {
    const container = document.getElementById('pathfinder-map-container');
    if (!container) return;

    if (!isFullscreen) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle map clicks for location selection and measurement
  const handleMapClick = async (event: any) => {
    // Handle measurement first
    if (isMeasuring) {
      handleMeasurementClick(event);
      return;
    }
    
    if (!isLocationSelectionMode || !onMapClick) return;
    
    const { lngLat } = event;
    const lat = lngLat.lat;
    const lon = lngLat.lng;
    
    // Reverse geocode to get address
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`
      );
      const data = await response.json();
      const address = data.features[0]?.place_name || '';
      
      onMapClick(lat, lon, address);
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      onMapClick(lat, lon);
    }
  };

  // Get unique students for color coding in view mode
  const uniqueStudents = useMemo(() => {
    if (viewMode === 'edit') return [];
    const students = routeEntries
      .map(e => (e as any).submission?.student_number)
      .filter(Boolean);
    return Array.from(new Set(students)).sort();
  }, [routeEntries, viewMode]);

  // Student color mapping
  const STUDENT_COLORS = [
    '#2196f3', '#f44336', '#4caf50', '#ff9800', '#9c27b0', 
    '#00bcd4', '#ffeb3b', '#e91e63', '#795548', '#607d8b',
  ];

  const getStudentColor = (studentNumber: string): string => {
    const index = uniqueStudents.indexOf(studentNumber);
    return STUDENT_COLORS[index % STUDENT_COLORS.length];
  };

  // Filter valid entries
    const validEntries = routeEntries.filter(entry => 
      entry.latitude !== 0 && entry.longitude !== 0 && entry.latitude && entry.longitude
    );

  // Recalculate map canvas when the container changes size (tab visibility, panel resize, etc.)
  useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    const RESIZE_SETTLE_MS = 120;

    const triggerResize = (delay = RESIZE_SETTLE_MS) => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resizeTimer = null;
        const map = mapRef.current?.getMap?.();
        if (map) {
          try {
            map.resize();
          } catch {
            // Map may be torn down during navigation.
          }
        }
      }, delay);
    };

    const handleWindowResize = () => triggerResize();
    window.addEventListener('resize', handleWindowResize);

    let observer: ResizeObserver | null = null;
    const container = mapContainerRef.current;
    if (container && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => triggerResize());
      observer.observe(container);
    }

    triggerResize(0);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      if (observer) observer.disconnect();
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, [mapLoaded]);

  return (
    <Box ref={mapContainerRef} id="pathfinder-map-container" sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: -110.9265,
          latitude: 32.2217,
          zoom: 11
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={getMapboxStyleUrl(mapStyle)}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        projection={{name: 'mercator'}}
        onClick={handleMapClick}
        cursor={isMeasuring ? 'crosshair' : (isLocationSelectionMode ? 'crosshair' : 'grab')}
        preserveDrawingBuffer={true}
        onLoad={() => {
          setMapLoaded(true);
          // Expose map instance for client-side exports (PowerPoint, etc.)
          const map = mapRef.current?.getMap();
          if (map) {
            (window as any).__pathfinderMap = map;
          }
          if (onMapReady && mapRef.current) {
            onMapReady(mapRef.current.getMap());
          }
        }}
        interactive={true}
        touchZoomRotate={true}
        dragRotate={true}
        dragPan={true}
        scrollZoom={true}
      >
        {/* Markers */}
        {validEntries.map((entry, index) => {
          const isSelected = index === selectedEntryIndex;
          let bgColor: string;
          
          if (viewMode === 'view' && (entry as any).submission?.student_number) {
            bgColor = getStudentColor((entry as any).submission.student_number);
          } else {
            bgColor = entry.route_type === 'Pre-route' ? '#2196f3' : '#f44336';
          }
          
          return (
            <Marker
              key={index}
              longitude={entry.longitude}
              latitude={entry.latitude}
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                if (viewMode === 'view' && onEntryClick) {
                  // Trigger flash animation
                  setFlashingMarkerId(index);
                  setTimeout(() => setFlashingMarkerId(null), 1000); // Flash for 1 second
                  
                  onEntryClick(entry);
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: viewMode === 'view' ? 'pointer' : 'default',
                }}
              >
                {/* Route component label - above marker */}
                <Box
                  sx={{
                    mb: 0.25,
                    fontSize: 9,
                    fontWeight: 600,
                    color: '#333',
                    opacity: 0.85,
                    textShadow: '0 0 2px #fff, 0 0 4px #fff, 1px 1px 1px rgba(255,255,255,0.8)',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.2px',
                  }}
                >
                  {entry.route_component}
                </Box>
                
                {/* Numbered circle - smaller and semi-transparent */}
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: bgColor,
                    opacity: isSelected ? 1 : 0.75,
                    border: isSelected ? '2px solid #ffeb3b' : '1.5px solid rgba(255,255,255,0.9)',
                    boxShadow: flashingMarkerId === index 
                      ? '0 0 20px 10px rgba(255, 235, 59, 1), 0 0 40px rgba(255, 235, 59, 0.8)'
                      : '0 1px 3px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: 10,
                    color: 'white',
                    transform: isSelected ? 'scale(1.3)' : (flashingMarkerId === index ? 'scale(1.4)' : 'none'),
                    transition: flashingMarkerId === index ? 'none' : 'transform 0.2s, opacity 0.2s',
                    animation: flashingMarkerId === index ? `${flashAnimation} 0.5s ease-out 3` : 'none',
                    '&:hover': {
                      opacity: 1,
                      transform: isSelected ? 'scale(1.3)' : 'scale(1.15)',
                    },
                  }}
                >
                  {index + 1}
                </Box>
              </Box>
            </Marker>
          );
        })}
        
        {/* Vehicle Observation Markers */}
        {vehicleObservations.map((vehicle, index) => {
          if (!vehicle.latitude || !vehicle.longitude) return null;
          const vehFlashKey = `vehicle-${vehicle.id || index}`;
          const isFlashing = flashingObsId === vehFlashKey;
          
          return (
            <Marker
              key={`vehicle-${index}`}
              longitude={vehicle.longitude}
              latitude={vehicle.latitude}
              anchor="bottom"
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {/* Vehicle number label - above marker */}
                <Box
                  sx={{
                    mb: 0.25,
                    fontSize: 9,
                    fontWeight: 600,
                    color: '#333',
                    opacity: 0.85,
                    textShadow: '0 0 2px #fff, 0 0 4px #fff, 1px 1px 1px rgba(255,255,255,0.8)',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.2px',
                  }}
                >
                  V{vehicle.vehicle_number}
                </Box>
                
                {/* Vehicle icon with red background */}
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: '#d32f2f',
                    opacity: isFlashing ? 1 : 0.75,
                    border: '1.5px solid rgba(255,255,255,0.9)',
                    boxShadow: isFlashing
                      ? '0 0 20px 10px rgba(255, 235, 59, 1), 0 0 40px rgba(255, 235, 59, 0.8)'
                      : '0 1px 3px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    transform: isFlashing ? 'scale(1.4)' : 'none',
                    transition: isFlashing ? 'none' : 'opacity 0.2s, transform 0.2s',
                    animation: isFlashing ? `${flashAnimation} 0.5s ease-out 3` : 'none',
                    '&:hover': {
                      opacity: 1,
                      transform: isFlashing ? 'scale(1.4)' : 'scale(1.15)',
                    },
                  }}
                >
                  <DirectionsCar sx={{ fontSize: 12 }} />
                </Box>
              </Box>
            </Marker>
          );
        })}
        
        {/* Individual Observation Markers */}
        {individualObservations.map((individual, index) => {
          if (!individual.latitude || !individual.longitude) return null;
          const indFlashKey = `individual-${individual.id || index}`;
          const isFlashing = flashingObsId === indFlashKey;
          
          return (
            <Marker
              key={`individual-${index}`}
              longitude={individual.longitude}
              latitude={individual.latitude}
              anchor="bottom"
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {/* Individual number label - above marker */}
                <Box
                  sx={{
                    mb: 0.25,
                    fontSize: 9,
                    fontWeight: 600,
                    color: '#333',
                    opacity: 0.85,
                    textShadow: '0 0 2px #fff, 0 0 4px #fff, 1px 1px 1px rgba(255,255,255,0.8)',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.2px',
                  }}
                >
                  I{individual.individual_number}
                </Box>
                
                {/* Person icon with orange background */}
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: '#ff9800',
                    opacity: isFlashing ? 1 : 0.75,
                    border: '1.5px solid rgba(255,255,255,0.9)',
                    boxShadow: isFlashing
                      ? '0 0 20px 10px rgba(255, 235, 59, 1), 0 0 40px rgba(255, 235, 59, 0.8)'
                      : '0 1px 3px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    transform: isFlashing ? 'scale(1.4)' : 'none',
                    transition: isFlashing ? 'none' : 'opacity 0.2s, transform 0.2s',
                    animation: isFlashing ? `${flashAnimation} 0.5s ease-out 3` : 'none',
                    '&:hover': {
                      opacity: 1,
                      transform: isFlashing ? 'scale(1.4)' : 'scale(1.15)',
                    },
                  }}
                >
                  <Person sx={{ fontSize: 12 }} />
                </Box>
              </Box>
            </Marker>
          );
        })}
        
        {/* GPS Track Lines — one Source/Layer per device with Pathfinder colors */}
        {deviceTrackGeoJSONById.map((track) => (
          <Source key={track.sourceId} id={track.sourceId} type="geojson" data={track.data}>
            <Layer
              id={track.layerId}
              type="line"
              layout={{
                'line-join': 'round',
                'line-cap': 'round',
              }}
              paint={{
                'line-color': track.color,
                'line-width': 3,
                'line-opacity': 0.85,
              }}
            />
          </Source>
        ))}
        
        {/* Dwell Locations - Student's stops (Pathfinder style: transparent red circles) */}
        {dwellLocations.length > 0 && (
          <Source
            id="dwell-locations"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: dwellLocations.map((dwell, index) => ({
                type: 'Feature',
                properties: {
                  id: index,
                  duration_minutes: dwell.duration_minutes,
                  address: dwell.address || ''
                },
                geometry: {
                  type: 'Point',
                  coordinates: [dwell.longitude, dwell.latitude]
                }
              }))
            }}
          >
            <Layer
              id="dwell-circles"
              type="circle"
              paint={{
                'circle-radius': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  8, 5,    // At zoom 8, 5px radius
                  12, 15,  // At zoom 12, 15px radius
                  16, 30,  // At zoom 16, 30px radius
                  20, 50   // At zoom 20, 50px radius
                ],
                'circle-color': '#ff0000',         // Red color like Pathfinder
                'circle-opacity': 0.3,             // Semi-transparent fill
                'circle-stroke-color': '#ff0000',
                'circle-stroke-width': 2,
                'circle-stroke-opacity': 0.8
              }}
            />
          </Source>
        )}
        
        {/* Measurement Line */}
        {measurementPoints.length >= 2 && (
          <Source
            id="measurement-line"
            type="geojson"
            data={{
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: measurementPoints
              }
            }}
          >
            <Layer
              id="measurement-line-layer"
              type="line"
              layout={{
                'line-join': 'round',
                'line-cap': 'round'
              }}
              paint={{
                'line-color': '#ff5722',
                'line-width': 3,
                'line-dasharray': [2, 2]
              }}
            />
          </Source>
        )}
        
        {/* Measurement Points */}
        {measurementPoints.map((point, index) => (
          <Marker
            key={`measure-${index}`}
            longitude={point[0]}
            latitude={point[1]}
            anchor="center"
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: index === 0 ? '#4caf50' : '#ff5722',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 8,
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              {index + 1}
            </Box>
          </Marker>
        ))}
        
        {/* Scale Control Only */}
        <ScaleControl position="bottom-left" maxWidth={100} />
      </Map>

      {/* Geocoder Container - Top Left */}
      <Box
        ref={geocoderContainerRef}
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          zIndex: 2,
          '& .mapboxgl-ctrl-geocoder': {
            minWidth: '300px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }
        }}
      />

      {/* Map Style Control - Top Left (below geocoder) 
          Note: MapStyleControl has its own positioning with 10px margin,
          so we override its position to be below the geocoder */}
      <Box sx={{ 
        position: 'absolute', 
        top: 54, 
        left: 10, 
        zIndex: 1,
        '& > div': { // Override MapStyleControl's positioning
          position: 'relative !important',
          top: 'auto !important',
          left: 'auto !important',
          margin: '0 !important',
        }
      }}>
        <MapStyleControl
          selectedMapStyle={mapStyle}
          onMapStyleChange={setMapStyle}
          position="top-left"
        />
      </Box>

      {/* Unified Control Panel - All Controls in One Place */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 1,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(4px)',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Control Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 0.5, gap: 0.25 }}>
          {/* Zoom In */}
          <Tooltip title="Zoom In" placement="left">
            <IconButton
              onClick={handleZoomIn}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: theme.palette.mode === 'dark' ? 'white' : '#333',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* Zoom Out */}
          <Tooltip title="Zoom Out" placement="left">
            <IconButton
              onClick={handleZoomOut}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: theme.palette.mode === 'dark' ? 'white' : '#333',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* Reset Compass */}
          <Tooltip title="Reset North" placement="left">
            <IconButton
              onClick={handleResetBearing}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: theme.palette.mode === 'dark' ? 'white' : '#333',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <CompassIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Divider sx={{ my: 0.25 }} />
          
          {/* Fullscreen */}
          <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} placement="left">
            <IconButton
              onClick={toggleFullscreen}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: theme.palette.mode === 'dark' ? 'white' : '#333',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          
          {/* My Location */}
          <Tooltip title="My Location" placement="left">
            <IconButton
              onClick={() => {
                if (mapRef.current) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      mapRef.current?.flyTo({
                        center: [position.coords.longitude, position.coords.latitude],
                        zoom: 15,
                        duration: 1500
                      });
                    },
                    (error) => console.error('Geolocation error:', error)
                  );
                }
              }}
              size="small"
              sx={{
                width: 32,
                height: 32,
                color: theme.palette.mode === 'dark' ? 'white' : '#333',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <MyLocationIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Divider sx={{ my: 0.25 }} />
          
          {/* 3D Mode Toggle */}
          <Tooltip title={is3DMode ? 'Disable 3D' : 'Enable 3D'} placement="left">
            <IconButton
              onClick={toggle3DMode}
              size="small"
              sx={{
                width: 32,
                height: 32,
                backgroundColor: is3DMode ? 'primary.main' : 'transparent',
                color: is3DMode ? 'white' : (theme.palette.mode === 'dark' ? 'white' : '#333'),
                '&:hover': {
                  backgroundColor: is3DMode ? 'primary.dark' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                },
              }}
            >
              {is3DMode ? <ThreeDRotation fontSize="small" /> : <MapIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          
          {/* Legend Toggle */}
          <Tooltip title={showLegendPanel ? 'Hide Legend' : 'Show Legend'} placement="left">
            <IconButton
              onClick={() => setShowLegendPanel(!showLegendPanel)}
              size="small"
              sx={{
                width: 32,
                height: 32,
                backgroundColor: showLegendPanel ? 'primary.main' : 'transparent',
                color: showLegendPanel ? 'white' : (theme.palette.mode === 'dark' ? 'white' : '#333'),
                '&:hover': {
                  backgroundColor: showLegendPanel ? 'primary.dark' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                },
              }}
            >
              <LayersIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* Map Style Selector */}
          <Tooltip title="Map Style" placement="left">
            <IconButton
              onClick={() => setShowStyleSelector(!showStyleSelector)}
              size="small"
              sx={{
                width: 32,
                height: 32,
                backgroundColor: showStyleSelector ? 'primary.main' : 'transparent',
                color: showStyleSelector ? 'white' : (theme.palette.mode === 'dark' ? 'white' : '#333'),
                '&:hover': {
                  backgroundColor: showStyleSelector ? 'primary.dark' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                },
              }}
            >
              <SatelliteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Divider sx={{ my: 0.25 }} />
          
          {/* Measurement Tool */}
          <Tooltip title={isMeasuring ? 'Stop Measuring' : 'Measure Distance'} placement="left">
            <IconButton
              onClick={toggleMeasurement}
              size="small"
              sx={{
                width: 32,
                height: 32,
                backgroundColor: isMeasuring ? '#ff5722' : 'transparent',
                color: isMeasuring ? 'white' : (theme.palette.mode === 'dark' ? 'white' : '#333'),
                '&:hover': {
                  backgroundColor: isMeasuring ? '#e64a19' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                },
              }}
            >
              <MeasureIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* Clear Measurement - Only show when measuring */}
          {measurementPoints.length > 0 && (
            <Tooltip title="Clear Measurement" placement="left">
              <IconButton
                onClick={clearMeasurement}
                size="small"
                sx={{
                  width: 32,
                  height: 32,
                  color: '#f44336',
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>
      
      {/* Map Style Selector Panel */}
      {showStyleSelector && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 10,
            right: 55,
            zIndex: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(4px)',
            borderRadius: 1,
            p: 1,
            minWidth: 120,
          }}
        >
          <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
            Map Style
          </Typography>
          {(['streets', 'satellite', 'satellite-streets', 'dark', 'light', 'outdoors'] as MapStyleType[]).map((style) => (
            <Box
              key={style}
              onClick={() => {
                setMapStyle(style);
                setShowStyleSelector(false);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 0.75,
                borderRadius: 0.5,
                cursor: 'pointer',
                backgroundColor: mapStyle === style ? 'primary.main' : 'transparent',
                color: mapStyle === style ? 'white' : 'text.primary',
                '&:hover': {
                  backgroundColor: mapStyle === style ? 'primary.dark' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                },
              }}
            >
              <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                {style.replace('-', ' ')}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Measurement Result Display */}
      {isMeasuring && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: showStyleSelector ? 190 : 55,
            zIndex: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(4px)',
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2,
            border: '2px solid #ff5722',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            📏 Measurement Mode
          </Typography>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {measurementPoints.length === 0 
              ? 'Click to start measuring' 
              : `${measurementPoints.length} point${measurementPoints.length > 1 ? 's' : ''}`
            }
          </Typography>
          {totalDistance > 0 && (
            <Typography variant="body2" color="primary.main" fontWeight={700} sx={{ mt: 0.5 }}>
              {formatDistance(totalDistance)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '10px' }}>
            Click map to add points. Click 📏 to finish.
          </Typography>
        </Box>
      )}

      {/* Entry Count - Bottom Center */}
      {validEntries.length > 0 && (
        <Chip
          label={`${validEntries.length} ${validEntries.length === 1 ? 'Location' : 'Locations'}`}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(4px)',
            border: `1px solid ${theme.palette.divider}`,
            fontWeight: 600,
            color: theme.palette.text.primary,
            '& .MuiChip-label': {
              color: theme.palette.text.primary,
              px: 1,
            },
          }}
        />
      )}

      {/* Unified Legend Panel - Top Left (below map style control) */}
      {showLegendPanel && (
        <Box
          sx={{
            position: 'absolute',
            top: 108,
            left: 10,
            zIndex: 1,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(4px)',
            maxWidth: 220,
            borderRadius: 1,
            boxShadow: 2,
            border: 1,
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Legend Header with Toggle */}
          <Box
            onClick={() => setLegendCollapsed(!legendCollapsed)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              },
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
              Legend
            </Typography>
            {legendCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
          </Box>
          
          {/* Legend Content - Collapsible */}
          {!legendCollapsed && (
            <Box sx={{ 
              p: 2, 
              pt: 0,
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto',
            }}>
              {/* GPS Track & Dwells Section */}
              {(deviceTracks.some((t) => t.points.length > 0) || dwellLocations.length > 0) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
                    My Route
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {deviceTracks
                      .filter((t) => t.points.length > 0)
                      .map((track) => (
                        <Box key={track.deviceId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 3,
                              backgroundColor: track.color,
                              borderRadius: 1,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: '11px' }} color="text.primary">
                            {(track.deviceType || 'device').charAt(0).toUpperCase() +
                              (track.deviceType || 'device').slice(1)}{' '}
                            ({track.points.length} pts)
                          </Typography>
                        </Box>
                      ))}
                    {dwellLocations.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 0, 0, 0.3)',
                            border: '2px solid #ff0000',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" sx={{ fontSize: '11px' }} color="text.primary">
                          Dwell Locations ({dwellLocations.length})
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Itinerary Section - View Mode */}
              {viewMode === 'view' && uniqueStudents.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
                    Itinerary Entries
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {uniqueStudents.map((student) => {
                      const color = getStudentColor(student);
                      const count = routeEntries.filter(e => (e as any).submission?.student_number === student).length;
                      return (
                        <Box key={student} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: color,
                              border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'white'}`,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: '11px' }} color="text.primary">
                            {student} ({count})
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  {/* Route Type */}
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#2196f3', border: '1px solid #ccc', flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ fontSize: '10px' }} color="text.secondary">Pre-route</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#f44336', border: '1px solid #ccc', flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ fontSize: '10px' }} color="text.secondary">Post-route</Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Observations Section */}
              {(vehicleObservations.length > 0 || individualObservations.length > 0) && (
                <Box>
                  <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
                    Observations
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {vehicleObservations.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#d32f2f',
                            border: '1.5px solid rgba(255,255,255,0.9)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            flexShrink: 0,
                          }}
                        >
                          <DirectionsCar sx={{ fontSize: 12 }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '11px' }} color="text.primary">
                          Vehicle ({vehicleObservations.length})
                        </Typography>
                      </Box>
                    )}
                    {individualObservations.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#ff9800',
                            border: '1.5px solid rgba(255,255,255,0.9)',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            flexShrink: 0,
                          }}
                        >
                          <Person sx={{ fontSize: 12 }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '11px' }} color="text.primary">
                          Individual ({individualObservations.length})
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Empty state */}
              {deviceTracks.every((t) => t.points.length === 0) && dwellLocations.length === 0 && uniqueStudents.length === 0 && vehicleObservations.length === 0 && individualObservations.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No data loaded on map
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ItineraryMapGL;
